/* Search autocomplete for the finder inputs (#partNumber, #MegaFinderQuery-*,
 * #ef-404-q). Two suggestion groups, both served from assets/ef-xref-index.json:
 *
 *   Products     — the 70 products, matched on their title and on the curated
 *                  alias groups ('ccp', 'swimclear', 'opal xl'). Selecting one
 *                  opens the PDP, the standard storefront autocomplete contract;
 *                  when what was typed names a housing whose brand has a page for
 *                  the cartridge (index products[].r), the row names that housing
 *                  and opens that brand's page ('sta-rite prc75' opens the Sta-Rite
 *                  page, not the Hayward-led product page), as site search does.
 *   Part numbers — the ~720 cross-reference numbers a shopper reads off an old
 *                  cartridge, which native search cannot see. Selecting one
 *                  SUBMITS A SEARCH (/search?q=<display>&type=product), never a
 *                  PDP jump — owner decision: a part number always shows a
 *                  results page, because one number can fit several products.
 *
 * Group order follows the query shape: digit-bearing queries lead with part
 * numbers, name-ish queries lead with products.
 *
 * Shape words (2026-10-15), as site search reads them (SHAPE WORDS in ef-search-xref.js):
 * "TC450 round" lists the round cartridges first and the non-round TC450 cartridge after
 * them; when the model typed is listed only for the other shape, the family's cartridge of
 * the shape typed with the nearest model number leads (TC450 round -> the round TC440).
 *
 * Replaces a part-number-only ranker whose last tier matched title tokens but
 * still rendered a PART NUMBER row — so typing 'opal' returned a list of
 * '817-xxxx' codes. Product rows are that tier's replacement.
 *
 * Index load is lazy: first focus, memoized, sessionStorage-cached keyed on the
 * versioned asset URL. Never on page load.
 */
(() => {
  'use strict';
  const me = document.currentScript;
  const indexUrl = me && me.dataset.indexUrl;
  if (!indexUrl) return;

  const MIN_CHARS = 2;
  const MAX_ROWS = 8;
  const MIN_PRIMARY = 5;
  const DEBOUNCE_MS = 120;

  let indexPromise = null;
  let parts = null;    // [{ key, display, handle, title, sku }]
  let products = null; // [{ handle, label, sku, aliases: [normalized], shape, nums, families }]
  let byHandle = null; // handle -> products row

  const strictKey = (s) => s.toUpperCase().replace(/[ \-®™]/g, '');
  // Mirrors aliasKey() in build-xref-index.mjs and ef-search-xref.js: '&' folds
  // to AND, everything else non-alphanumeric drops, so 'swim clear' and
  // 'swimclear' collapse to one form.
  const aliasKey = (s) => String(s).toUpperCase().replace(/&/g, 'AND').replace(/[^A-Z0-9]/g, '');
  const words = (s) => String(s).toLowerCase().replace(/&/g, ' and ').split(/[^a-z0-9]+/).filter(Boolean);
  const hasDigit = (s) => /[0-9]/.test(s);
  // Mirrors shapeOf(), withoutShape() and modelNumbers() in ef-search-xref.js: 'round', 'other'
  // (non-round) or '' for one text or a list; `typed` reads "original" as non-round.
  const shapeOf = (texts, typed) => {
    let round = false;
    let other = false;
    for (const text of [].concat(texts)) {
      const w = words(text);
      w.forEach((t, i) => {
        if (t === 'round') { if (w[i - 1] === 'non') other = true; else round = true; }
        else if (t === 'oval' || t === 'nonround' || (typed && t === 'original')) other = true;
      });
    }
    return round === other ? '' : round ? 'round' : 'other';
  };
  const withoutShape = (s) => String(s).replace(/\bnon[\s-]?round\b|\b(?:round|oval|original)\b/gi, ' ').replace(/\s+/g, ' ').trim();
  const modelNumbers = (s) => (String(s).replace(/\([^)]*\)/g, ' ').match(/\d{2,}/g) || []).map(Number);
  // A typed word written without the name's space or hyphen ('prc75' for 'PRC 75',
  // 'pxc75' for 'PXC-75', 'starite' for 'Sta-Rite') equals whole adjacent words of
  // the title, never part of one.
  const joinsWords = (w, tw) => {
    for (let i = 0; i < tw.length; i++) {
      let run = tw[i];
      for (let j = i + 1; j < tw.length && run.length < w.length; j++) {
        run += tw[j];
        if (run === w) return true;
      }
    }
    return false;
  };

  // Titles are SEO-shaped ('Excel Filters XLS-937 1-Pack Replacement Filter
  // Cartridge, Compatible with Waterco Opal XL 90 ... Replaces Waterco 701041')
  // and far too long for a dropdown row. What a shopper needs is the bit that
  // says what it FITS, so strip the house boilerplate off both ends.
  const compact = (t) => String(t)
    .replace(/^Excel Filters\s+\S+\s+/i, '')
    .replace(/^\d+-Pack\s+/i, '')
    .replace(/^Replacement Filter Cartridges?[,.]?\s*/i, '')
    .replace(/^(?:Compatible with|for)\s+/i, '')
    .replace(/[,.]?\s*Replaces\s.*$/i, '')
    .replace(/\s*(Replacement\s+)?Filter Cartridges?\s*$/i, '')
    .replace(/\s*[|\-–]\s*$/, '')
    .trim();

  const loadIndex = () => {
    if (indexPromise) return indexPromise;
    indexPromise = Promise.resolve()
      .then(() => {
        const cacheKey = 'ef-xref-index:' + indexUrl;
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) return JSON.parse(cached);
        } catch (e) { /* storage unavailable */ }
        return fetch(indexUrl, { credentials: 'same-origin', priority: 'low' })
          .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
          .then((text) => {
            try { sessionStorage.setItem(cacheKey, text); } catch (e) { /* quota */ }
            return JSON.parse(text);
          });
      })
      .then((index) => {
        if (!index || index.v !== 1) return null;

        parts = Object.keys(index.keys).sort().map((key) => {
          const listed = index.keys[key].map((i) => index.products[i]);
          const first = listed[0];
          const many = [...new Set(listed.map((p) => p.s))];
          return { key, display: index.display[key] || key, handle: first.h, title: many.length > 1 ? 'listed for ' + many.length + ' cartridges' : String(first.t).split(' | ')[0], sku: many.join(', ') };
        });

        // Alias terms are per-group; invert them onto the products they claim so
        // a product row can be found by 'ccp' as well as by its title.
        const aliasesFor = index.products.map(() => []);
        for (const g of index.aliasGroups || []) {
          for (const i of g.p) {
            if (aliasesFor[i]) aliasesFor[i].push.apply(aliasesFor[i], g.t);
          }
        }
        // What a product row is found by: the housings excel.fit lists for it (index.housings),
        // falling back to the title only for an index without them.
        const fitsFor = index.products.map(() => []);
        (index.housings || []).forEach((h, at) => {
          for (const i of h.p) {
            if (!fitsFor[i]) continue;
            // The page led by this housing's brand for the cartridge (products[].r), as site search uses it.
            const pages = index.products[i].r || {};
            const col = Object.keys(pages).find((c) => pages[c].indexOf(at) !== -1) || '';
            fitsFor[i].push({ name: h.n, url: col ? '/collections/' + col + '/products/' + index.products[i].h : '' });
          }
        });
        products = index.products.map((p, i) => ({
          handle: p.h,
          sku: p.s,
          label: compact(String(p.t).split(' | ')[0]) || p.t,
          title: [p.t].concat(fitsFor[i].map((f) => f.name)).join(' , '),
          aliases: aliasesFor[i] || [],
          pages: fitsFor[i].filter((f) => f.url),
          // Shape words: the cartridge's shape, its housings' model numbers, its curated families.
          shape: shapeOf([p.t].concat(fitsFor[i].map((f) => f.name))),
          nums: [].concat(...fitsFor[i].map((f) => modelNumbers(f.name))),
          families: (index.aliasGroups || []).filter((g) => g.p.indexOf(i) !== -1).map((g) => g.p),
        }));
        byHandle = new Map(products.map((p) => [p.handle, p]));
        return true;
      })
      .catch(() => null);
    return indexPromise;
  };

  const suggestParts = (q) => {
    const tiers = [[], [], [], []];
    for (const row of parts) {
      if (row.key === q) tiers[0].push(row);
      else if (row.key.startsWith(q)) tiers[1].push(row);
      else if (strictKey(row.display).startsWith(q)) tiers[2].push(row);
      else if (strictKey(row.sku).startsWith(q)) tiers[3].push(row);
    }
    const out = [];
    const seen = new Set();
    for (const tier of tiers) {
      for (const row of tier) {
        if (seen.has(row.key)) continue;
        seen.add(row.key);
        out.push({ kind: 'part', key: row.key, display: row.display, sku: row.sku, title: row.title });
      }
    }
    return out;
  };

  const suggestProducts = (raw) => {
    const ak = aliasKey(raw);
    const qw = words(raw);
    if (!ak) return [];
    const namesWords = (text) => {
      const tw = words(text);
      return qw.length && qw.every((w) => tw.indexOf(w) !== -1 || (w.length >= 3 && !hasDigit(w) && tw.some((t) => t.indexOf(w) === 0)) || joinsWords(w, tw));
    };
    const tiers = [[], [], []];
    for (const p of products) {
      if (ak.length >= 3 && p.aliases.indexOf(ak) !== -1) tiers[0].push(p);
      else if (ak.length >= 3 && !hasDigit(ak) && aliasKey(p.title).indexOf(ak) !== -1) tiers[1].push(p);
      else if (namesWords(p.title)) tiers[2].push(p);
    }
    const out = [];
    const seen = new Set();
    for (const tier of tiers) {
      for (const p of tier) {
        if (seen.has(p.handle)) continue;
        seen.add(p.handle);
        // A housing whose brand has a page for this cartridge, named by what was typed ("sta-rite
        // prc75", "starite"): the row names that housing and opens that brand's page, as site search does.
        const page = p.pages.find((f) => namesWords(f.name) || (ak.length >= 3 && !hasDigit(ak) && aliasKey(f.name).indexOf(ak) !== -1));
        out.push({ kind: 'product', handle: p.handle, display: page ? page.name : p.label, sku: p.sku, url: page ? page.url : '' });
      }
    }
    return out;
  };

  // "CL-460", "C-900", "CS-100" typed with the hyphen: the housing names list CL460, C900, CS100.
  const joinCodes = (s) => s.replace(/([A-Za-z])[\s-]+(?=\d)/g, '$1');
  const productRows = (text) => {
    const rows = suggestProducts(text);
    return !rows.length && joinCodes(text) !== text ? suggestProducts(joinCodes(text)) : rows;
  };

  // Shape words (header): rows of the shape typed first, then rows without a shape, then rows of
  // the other shape. None of the shape typed: the family's cartridges of that shape with the
  // model number nearest the one typed lead instead. Rows are reordered or added, never dropped.
  const byShape = (typed, rows) => {
    const want = shapeOf(typed, true);
    const rest = want ? withoutShape(typed) : '';
    if (!rest) return rows;
    const shapeAt = (row) => (byHandle.get(row.handle) || {}).shape || '';
    let list = rows;
    if (!list.some(shapeAt)) {
      // "TC330 oval" names no row as typed; "TC330" does.
      const again = productRows(rest);
      if (!again.some(shapeAt)) return rows;
      list = again;
    }
    const same = list.filter((row) => shapeAt(row) === want);
    const against = list.filter((row) => shapeAt(row) && shapeAt(row) !== want);
    let lead = same;
    if (!same.length) {
      const from = against.map((row) => byHandle.get(row.handle));
      const near = modelNumbers(rest).length ? modelNumbers(rest) : [].concat(...from.map((p) => p.nums));
      let best = Infinity;
      const seen = new Set();
      for (const p of from) {
        for (const family of p.families) {
          for (const j of family) {
            const c = products[j];
            if (!c || seen.has(c.handle) || from.indexOf(c) !== -1 || c.shape !== want) continue;
            seen.add(c.handle);
            let d = Infinity;
            for (const n of c.nums) for (const m of near) d = Math.min(d, Math.abs(n - m));
            if (d === Infinity) continue;
            const row = { kind: 'product', handle: c.handle, display: c.label, sku: c.sku, url: '' };
            if (d < best) { best = d; lead = [row]; } else if (d === best) lead.push(row);
          }
        }
      }
    }
    return lead.concat(list.filter((row) => !shapeAt(row)), against);
  };

  const suggest = (raw) => {
    if (!parts || !products) return [];
    const trimmed = raw.trim();
    if (trimmed.length < MIN_CHARS) return [];
    const prod = byShape(trimmed, productRows(trimmed));
    const part = suggestParts(strictKey(trimmed));

    // A code-shaped query wants part numbers first; a name wants products.
    const partsLead = hasDigit(trimmed);
    const primary = partsLead ? part : prod;
    const secondary = partsLead ? prod : part;

    const take1 = primary.slice(0, Math.max(MIN_PRIMARY, MAX_ROWS - secondary.length));
    return take1.concat(secondary.slice(0, MAX_ROWS - take1.length));
  };

  // Inverted highlighting: what the shopper typed stays plain, the completion is
  // bold, so the eye lands on what is NEW. Falls back to no emphasis when the
  // match is not a clean prefix.
  const prefixSplit = (display, typed) => {
    const t = strictKey(typed);
    if (!t || !strictKey(display).startsWith(t)) return null;
    let n = 0;
    for (let i = 0; i < display.length; i++) {
      if (strictKey(display[i]).length) n++;
      if (n >= t.length) return [display.slice(0, i + 1), display.slice(i + 1)];
    }
    return [display, ''];
  };

  const paintPart = (el, display, typed) => {
    const split = prefixSplit(display, typed);
    if (!split) { el.textContent = display; return; }
    el.appendChild(document.createTextNode(split[0]));
    if (split[1]) {
      const b = document.createElement('b');
      b.className = 'ef-suggest__completion';
      b.textContent = split[1];
      el.appendChild(b);
    }
  };

  const paintProduct = (el, label, typed) => {
    const needle = typed.trim().toLowerCase();
    const i = needle ? label.toLowerCase().indexOf(needle) : -1;
    if (i < 0) { el.textContent = label; return; }
    el.appendChild(document.createTextNode(label.slice(0, i)));
    const b = document.createElement('b');
    b.className = 'ef-suggest__completion';
    b.textContent = label.slice(i, i + needle.length);
    el.appendChild(b);
    el.appendChild(document.createTextNode(label.slice(i + needle.length)));
  };

  const attach = (input) => {
    const form = input.closest('form');
    if (!form) return;

    const listId = 'ef-suggest-list-' + Math.random().toString(36).slice(2, 8);
    const list = document.createElement('ul');
    list.id = listId;
    list.className = 'ef-suggest__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    // The listbox must be a DOM child of the form: the finder's outside-click
    // closer (theme.liquid) checks container.contains(event.target), and the
    // finder panel is overflow:scroll, so the list renders in normal flow.
    form.classList.add('ef-suggest__form');
    input.insertAdjacentElement('afterend', list);

    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    form.appendChild(status);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', 'off');

    let rows = [];
    let optionEls = []; // parallel to rows — group headers are NOT options, so
                        // list.children can no longer be indexed by activeIdx.
    let activeIdx = -1;
    let timer = null;
    let swallowEscUp = false;

    const close = () => {
      list.hidden = true;
      list.innerHTML = '';
      rows = [];
      optionEls = [];
      activeIdx = -1;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };

    const choose = (row) => {
      close();
      window.location.href = row.kind === 'product'
        ? row.url || '/products/' + row.handle
        : '/search?q=' + encodeURIComponent(row.display) + '&type=product';
    };

    const render = (typed) => {
      list.innerHTML = '';
      optionEls = [];
      let lastKind = null;
      rows.forEach((row, i) => {
        if (row.kind !== lastKind) {
          lastKind = row.kind;
          const head = document.createElement('li');
          head.className = 'ef-suggest__group';
          head.setAttribute('role', 'presentation');
          head.textContent = row.kind === 'product' ? 'Products' : 'Part numbers';
          list.appendChild(head);
        }
        const li = document.createElement('li');
        li.id = listId + '-opt-' + i;
        li.className = 'ef-suggest__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');

        const primary = document.createElement('span');
        primary.className = 'ef-suggest__part';
        if (row.kind === 'product') paintProduct(primary, row.display, typed);
        else paintPart(primary, row.display, typed);

        const sub = document.createElement('span');
        sub.className = 'ef-suggest__product';
        sub.textContent = row.kind === 'product' ? row.sku : row.sku + ' · ' + row.title;

        li.appendChild(primary);
        li.appendChild(sub);
        // mousedown, not click — click fires after blur closes the list.
        li.addEventListener('mousedown', (e) => { e.preventDefault(); choose(row); });
        list.appendChild(li);
        optionEls.push(li);
      });
      list.hidden = rows.length === 0;
      input.setAttribute('aria-expanded', rows.length ? 'true' : 'false');
      status.textContent = rows.length ? rows.length + ' suggestions' : '';
    };

    const setActive = (idx) => {
      if (activeIdx >= 0 && optionEls[activeIdx]) {
        optionEls[activeIdx].classList.remove('is-active');
        optionEls[activeIdx].setAttribute('aria-selected', 'false');
      }
      activeIdx = idx;
      if (idx >= 0 && optionEls[idx]) {
        optionEls[idx].classList.add('is-active');
        optionEls[idx].setAttribute('aria-selected', 'true');
        // aria-activedescendant only — the input value is NOT overwritten while arrowing.
        input.setAttribute('aria-activedescendant', optionEls[idx].id);
        optionEls[idx].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    };

    input.addEventListener('focus', () => { loadIndex(); }, { once: true });

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        loadIndex().then(() => {
          const typed = input.value;
          rows = suggest(typed);
          activeIdx = -1;
          render(typed);
        });
      }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (event) => {
      if (list.hidden) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); setActive(Math.min(activeIdx + 1, rows.length - 1)); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(Math.max(activeIdx - 1, -1)); }
      else if (event.key === 'Enter' && activeIdx >= 0) { event.preventDefault(); choose(rows[activeIdx]); }
      else if (event.key === 'Escape') {
        // One Esc dismisses the dropdown only. The finder's closers listen on
        // KEYUP at document level, and by then the list is already hidden — the
        // flag carries "this Esc was consumed" across to the keyup phase.
        event.stopPropagation();
        swallowEscUp = true;
        close();
      }
    });
    input.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && swallowEscUp) {
        event.stopPropagation();
        swallowEscUp = false;
      }
    });

    input.addEventListener('blur', () => { setTimeout(close, 100); });
  };

  const mount = () => {
    const inputs = [
      document.getElementById('partNumber'),
      document.getElementById('ef-404-q'),
      ...document.querySelectorAll('input[id^="MegaFinderQuery-"]'),
    ].filter(Boolean);
    inputs.forEach(attach);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
